import { isNil } from 'lodash';
import type { SlopeInfo, SlopeToolState } from '../types';

export const calculateSlope = (
  pointA: SlopeToolState['pointA'],
  pointB: SlopeToolState['pointB'],
): SlopeInfo | null => {
  if (isNil(pointA) || isNil(pointB)) {
    return null;
  }

  const deltaY = pointB.elevation - pointA.elevation;
  const deltaX = Math.abs(pointB.mileage - pointA.mileage);

  if (!deltaX) {
    return {
      horizontalDistance: 0,
      verticalDistance: Math.abs(deltaY),
      slopePercentage: Infinity,
      slopeAngle: 90,
    };
  }

  const slope = deltaY / deltaX;
  const slopeAngle = +(Math.atan(slope) * (180 / Math.PI)).toFixed(2);
  const slopePercentage = +(slope * 100).toFixed(2);

  return {
    horizontalDistance: +deltaX.toFixed(2),
    verticalDistance: +Math.abs(deltaY).toFixed(2),
    slopeAngle,
    slopePercentage,
  };
};

export const getSlopeDetailsText = (slopeInfo: SlopeInfo | null) => {
  if (isNil(slopeInfo)) {
    return {
      deltaX: '--',
      deltaY: '--',
      slope: '--',
    };
  }

  const degreeSign = String.fromCharCode(176);
  const slopeText =
    slopeInfo.slopePercentage === Infinity
      ? `90${degreeSign} (∞%)`
      : `${slopeInfo.slopeAngle}${degreeSign} - ${slopeInfo.slopePercentage}%`;

  return {
    deltaX: `${slopeInfo.horizontalDistance} m`,
    deltaY: `${slopeInfo.verticalDistance} m`,
    slope: slopeText,
  };
};

export const getPointText = (
  point: SlopeToolState['pointA'] | SlopeToolState['pointB'],
): string => {
  if (isNil(point)) {
    return 'Please select';
  }

  return `${point.mileage.toFixed(2)} m`;
};
